import { ChangeDetectionStrategy, Component, Inject, OnInit, Optional } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ISites } from '@df/core/df-core.interfaces';
import { ISizeName } from '@df/core/service/client';
import { MultisiteService } from '@df/core/service/multisite';
import { ImageModule } from '@df/module-image/image.module';
import { Basket } from '@df/session/api/basket/basket';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NBasketCarryOverModal } from '../../basket/carry-over/basket-carry-over-modal.component';
import { RadioComponent } from '../../ui/form/radio';
import { NMultisiteModal } from './multisite-modal.interfaces';
const storeOptionsFn = () =>
    (window['app'].multisite as MultisiteService).all.map(i => ({
        key: i.siteId,
        title: i.name
    }));
/**
 * IMPORTANT
 *
 * Add this component to ngModule entry components in app.module to avoid tree shaking exclusion
 * entryComponents: [MultisiteModalComponent]
 */
@DfCmsModal({
    id: NMultisiteModal.componentId,
    position: 'custom',
    name: 'Multisite info',
    config: (sizeId: ISizeName) => ({
        closeOnNavigation: true,
        height: '100%',
        minWidth: undefined,
        maxWidth: undefined,
        width: sizeId === 'S' ? '100%' : '31.5em',
        position: {
            top: sizeId === 'S' ? '' : '0',
            bottom: sizeId === 'S' ? '0' : '',
            left: '0'
        },
        transformOptions: sizeId === 'S' ? 'translate3d(0%, 100%, 0px)' : 'translate3d(-100%, 0px, 0px)',
        transitionOptions: '500ms ease-in-out'
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeId => (sizeId === 'S' ? ['S'] : ['M', 'L', 'X']),
    blockGroup: 'multisiteModal',
    priority: 30
})
@ICmsComponent<MultisiteModalComponent>({
    id: NMultisiteModal.componentId,
    name: 'Multisite Popup',
    description: 'Appears when a user clicks on a popup link. Offers the user to change store.',
    block: {
        url: NMultisiteModal.componentId,
        scope: 'website'
    },
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'testing',
            title: 'CMS testing setup'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cmsCountryCode',
            title: 'Force country code',
            description: 'Put any country code to preview popup in that variant'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'variants',
            title: 'Variant',
            description: 'First variant that matches user country code is used for display.',
            itemName: 'Variant',
            required: true,
            defaultSize: 1,
            itemNameField: 'countries',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'countries',
                    title: 'Countries',
                    description: 'Coma separated list of 2-char country-codes, see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'defaultSiteId',
                    title: 'Default site',
                    options: storeOptionsFn,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'label-general',
                    title: 'General'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'heading',
                    title: 'Top heading',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy',
                    title: 'Top copy',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'buttonCopy',
                    title: 'Confirmation button copy',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'stores',
                    title: 'Store links',
                    itemName: 'Store link',
                    required: true,
                    defaultSize: 1,
                    itemNameField: 'siteId',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            key: 'siteId',
                            title: 'Website',
                            description:
                                'Please select website to link to. If website is off the link will be skipped. Make sure to select website only once.',
                            options: storeOptionsFn,
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.image,
                            key: 'icon',
                            title: 'Icon'
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'title',
                            title: 'Title',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'subtitle',
                            title: 'Subtitle'
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'details',
                            title: 'Details'
                        }
                    ]
                }
            ]
        }
    ]
})
@Component({
    selector: 'multisite_modal',
    templateUrl: './multisite-modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./multisite-modal.scss'],
    standalone: true,
    imports: [ICmsClientModule, RadioGroupDirective, FormsModule, RadioComponent, ImageModule]
})
export class MultisiteModalComponent extends UiComponent implements OnInit, NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content!: NMultisiteModal.IBlockContent;

    constructor(
        private multisiteRedirectService: MultisiteRedirectService,
        private modalRef: ModalRef<MultisiteModalComponent>,
        private multisiteService: MultisiteService,
        private basket: Basket,
        private dfModalService: DfModalService,
        private iCmsBlockResource: ICmsBlockResource,
        @Optional() @Inject(MODAL_DATA) public data: NMultisiteModal.IInitiatorData
    ) {
        super();
    }
    /**
     * selected store we will redirect to
     */
    selectedStore?: keyof ISites;

    override ngOnInit(): void {
        super.ngOnInit();
        this.modalRef.beforeClosed().subscribe(() => this.confirm());
    }

    onModelChange() {
        // super.onModelChange();
        const expectedSelection = this.contentVariant?.defaultSiteId || '';
        const stores = this.stores;
        this.pick(stores.find(i => i.siteId === expectedSelection) || stores[0]);
    }

    // protected _isEmpty() {
    //     if (super._isEmpty()) {
    //         return true;
    //     }
    //     if (!this.contentVariant?.stores?.length) {
    //         return true;
    //     }
    //     return false;
    // }

    /**
     * Retrieves a variant matching currently previewed content
     */
    get contentVariant(): NMultisiteModal.IBlockContentVariant | undefined {
        const variants = this.content?.variants;
        if (!variants) {
            return undefined;
        }
        const code = ((this.client.isCms && this.content.cmsCountryCode) || this.data.matchingCountry || '').toUpperCase();
        return (
            (code &&
                variants.find(variant =>
                    variant.countries
                        ?.toUpperCase()
                        .split(',')
                        .find(countryCode => countryCode.trim() === code)
                )) ||
            variants[0]
        );
    }

    /**
     * List of stores
     * only enabled ones are allowed
     */
    get stores() {
        return this.contentVariant?.stores.filter(i => !!this.multisiteService.toMultisiteItem(i.siteId)) || [];
    }

    pick(store: NMultisiteModal.IBlockContentStore | string) {
        this.selectedStore = store ? (typeof store === 'string' ? store : store.siteId) : undefined;
        this.detectChanges();
    }
    /**
     * Confirm location change
     */
    confirm(): void {
        // do not show again
        // this.disableGlobal();
        // jump to selected website
        this.goTo(<string>this.selectedStore);
    }

    async goTo(siteId: number | string, $event?: Event) {
        const block = (await this.iCmsBlockResource.retrieve(
            { bareUrl: NBasketCarryOverModal.blockId },
            undefined
        )) as NBasketCarryOverModal.Block;
        const content = block?.data?.content;
        if (content && !content.styles?.hidden && this.basket.items.length && siteId && siteId !== this.multisiteService.siteId) {
            try {
                this.dfModalService.open(undefined, NBasketCarryOverModal.blockId, undefined, { variant: 'canCarry', siteId, $event });
            } catch (e) {
                $event?.preventDefault();
            }
        } else if (this.multisiteRedirectService.to(siteId)) {
            $event?.preventDefault();
        } else {
            this.close();
        }
    }

    protected close(): void {
        this.modalRef.close();
    }

    // onModelChange() {
    //     super.onModelChange();

    //     const expectedSelection = this.contentVariant?.defaultSiteId || '';
    //     const stores = this.stores;

    //     this.pick(stores.find(i => i.siteId === expectedSelection) || stores[0]);
    // }
}
